import type { FC } from 'react'
import type { SceneProps } from '@/components/landing/games/scene-ui'

import { useState } from 'react'
import { cn } from '@/lib/utils'
import { range, shuffle } from '@/lib/random'
import { useSequence } from '@/hooks/use-sequence'
import { SceneBadge, SceneStat } from '@/components/landing/games/scene-ui'

const NUMBERS = range(40, 1)
const PICKS = 10

const POSTER_PICKS = [3, 7, 12, 15, 19, 22, 28, 31, 36, 40]
const POSTER_DRAWN = [7, 9, 15, 21, 25, 31, 33, 38, 40, 2]

const KenoScene: FC<SceneProps> = ({ active }) => {
    const [picks, setPicks] = useState<number[]>(POSTER_PICKS)
    const [drawn, setDrawn] = useState<number[]>(POSTER_DRAWN)

    useSequence(active, async ({ wait }) => {
        const nextPicks = shuffle(NUMBERS).slice(0, PICKS)
        const draw = shuffle(NUMBERS).slice(0, PICKS)

        setPicks(nextPicks)
        setDrawn([])

        if (!(await wait(800))) return

        for (let i = 0; i < draw.length; i++) {
            setDrawn(draw.slice(0, i + 1))
            if (!(await wait(240))) return
        }

        await wait(2000)
    })

    const picked = new Set(picks)
    const drawnSet = new Set(drawn)
    const hits = picks.filter(number => drawnSet.has(number)).length
    const finished = drawn.length === PICKS

    return (
        <div className='absolute inset-0 flex flex-col px-6 pb-5 pt-14'>
            <div className='grid flex-1 grid-cols-8 content-center gap-1'>
                {NUMBERS.map(number => {
                    const isPicked = picked.has(number)
                    const isDrawn = drawnSet.has(number)
                    const hit = isPicked && isDrawn

                    return (
                        <div
                            key={number}
                            className={cn(
                                'grid aspect-[1.25] place-items-center rounded-md font-mono text-[10px] tabular-nums transition-all duration-300',
                                hit && 'scale-105 bg-ember font-semibold text-ink shadow-[0_0_22px_-4px_rgba(255,91,31,0.85)]',
                                !hit && isPicked && 'bg-[#25252c] text-bone ring-1 ring-inset ring-bone/25',
                                !hit && !isPicked && isDrawn && 'bg-white/[0.03] text-ember-soft ring-1 ring-inset ring-ember/35',
                                !isPicked && !isDrawn && 'bg-white/[0.035] text-steel/50'
                            )}
                        >
                            {number}
                        </div>
                    )
                })}
            </div>

            <div className='mt-3 flex items-end justify-between gap-3'>
                <div className='flex gap-6'>
                    <SceneStat label='Hits' value={`${hits} of ${PICKS}`} tone={hits >= 4 ? 'ember' : 'default'} />
                    <SceneStat label='Drawn' value={`${drawn.length} of ${PICKS}`} />
                </div>

                <SceneBadge tone={finished ? (hits >= 4 ? 'ember' : 'muted') : 'default'}>
                    {finished ? `${hits} ${hits === 1 ? 'hit' : 'hits'}` : 'Drawing'}
                </SceneBadge>
            </div>
        </div>
    )
}

export default KenoScene
